import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { feature } from "topojson-client";
import { geoMercator, geoPath } from "d3-geo";
import { MapPin, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/site/motion";
import { ZONES, SERVICES } from "@/data/content";
import topo from "@/data/italy-regions.topo.json";

const W = 520;
const H = 640;

export default function CoverageMap({ compact = false }) {
    const { t, lang } = useLang();
    const m = t.mapSection;
    const navigate = useNavigate();
    const [activeId, setActiveId] = useState(ZONES[0]?.id);

    const { paths, markers } = useMemo(() => {
        const geo = feature(topo, topo.objects.regions);
        const projection = geoMercator().fitExtent([[24, 20], [W - 24, H - 20]], geo);
        const pathGen = geoPath(projection);
        const paths = geo.features.map((f, i) => ({ id: i, d: pathGen(f) }));
        const markers = ZONES.map((z) => {
            const [x, y] = projection([z.lng, z.lat]);
            return { ...z, x, y };
        });
        return { paths, markers };
    }, []);

    const active = ZONES.find((z) => z.id === activeId) || ZONES[0];
    const catName = (slug) => SERVICES.find((s) => s.slug === slug)?.[lang].name || slug;
    const regions = Array.from(new Set(ZONES.map((z) => z[lang].region)));

    if (compact) {
        return (
            <section data-testid="coverage-map-compact" className="py-24 md:py-32 bg-cb-darker cb-grid text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{m.chapter}</Reveal>
                        <Reveal as="h2" delay={0.3} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight">{m.title}</Reveal>
                        <Reveal delay={0.5} className="mt-6 text-slate-300 max-w-md">{m.sub}</Reveal>
                        <Reveal delay={0.7} className="mt-6 flex flex-wrap gap-2">
                            {ZONES.map((z) => (
                                <span key={z.id} className="text-xs border border-white/15 text-white/70 px-3 py-1.5">{z[lang].name}</span>
                            ))}
                        </Reveal>
                        <Reveal delay={0.9}>
                            <button
                                data-testid="map-portal-btn"
                                onClick={() => navigate("/progetti")}
                                className="cb-glow mt-8 inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-4 font-medium transition-colors"
                            >
                                {t.portfolio.view} <ArrowRight size={18} />
                            </button>
                        </Reveal>
                    </div>

                    <Reveal delay={0.4} className="relative w-full max-w-[360px] mx-auto">
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={m.title}>
                            <g>
                                {paths.map((p) => (
                                    <path key={p.id} d={p.d} fill="#1E293B" stroke="rgba(148,163,184,0.18)" strokeWidth={0.6} />
                                ))}
                            </g>
                            <g>
                                {markers.map((mk) => (
                                    <g key={mk.id} transform={`translate(${mk.x},${mk.y})`} onClick={() => navigate("/progetti")}
                                        data-testid={`map-mini-marker-${mk.id}`} className="cursor-pointer">
                                        <title>{mk[lang].name}</title>
                                        <motion.circle r={6} fill="none" stroke="#0EA5E9" strokeWidth={1.5}
                                            initial={{ r: 6, opacity: 0.7 }} animate={{ r: 18, opacity: 0 }}
                                            transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }} />
                                        <circle r={6} fill="#38BDF8" stroke="#020617" strokeWidth={1.5} className="hover:fill-cb-accent transition-colors" />
                                        <circle r={2.5} fill="#fff" />
                                    </g>
                                ))}
                            </g>
                        </svg>
                    </Reveal>
                </div>
            </section>
        );
    }

    return (
        <section data-testid="coverage-map-section" className="py-24 md:py-32 bg-cb-darker cb-grid text-white overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{m.chapter}</Reveal>
                        <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight">{m.title}</Reveal>
                    </div>
                    <Reveal delay={0.6} className="text-slate-300 max-w-md">{m.sub}</Reveal>
                </div>

                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    {/* Map */}
                    <Reveal className="relative">
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[640px]" role="img" aria-label={m.title}>
                            <g>
                                {paths.map((p) => (
                                    <path key={p.id} d={p.d} fill="#1E293B" stroke="rgba(148,163,184,0.18)" strokeWidth={0.6} />
                                ))}
                            </g>
                            <g>
                                {markers.map((mk) => {
                                    const isActive = mk.id === activeId;
                                    return (
                                        <g key={mk.id} transform={`translate(${mk.x},${mk.y})`}
                                            onClick={() => setActiveId(mk.id)}
                                            data-testid={`map-marker-${mk.id}`}
                                            className="cursor-pointer">
                                            {isActive && (
                                                <motion.circle
                                                    r={6} fill="none" stroke="#0EA5E9" strokeWidth={1.5}
                                                    initial={{ r: 6, opacity: 0.8 }}
                                                    animate={{ r: 20, opacity: 0 }}
                                                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                                                />
                                            )}
                                            <circle r={isActive ? 7 : 5} fill={isActive ? "#0EA5E9" : "#38BDF8"} stroke="#020617" strokeWidth={1.5} className="transition-all" />
                                            <circle r={isActive ? 3 : 2} fill="#fff" />
                                        </g>
                                    );
                                })}
                            </g>
                        </svg>
                        <p className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400">
                            <MapPin size={14} className="text-cb-accent" /> {m.hint}
                        </p>
                    </Reveal>

                    {/* Panel */}
                    <div>
                        {/* Zone selector chips */}
                        <div className="flex flex-wrap gap-2 mb-8 max-h-44 overflow-y-auto pr-1">
                            {ZONES.map((z) => (
                                <button
                                    key={z.id}
                                    data-testid={`map-chip-${z.id}`}
                                    onClick={() => setActiveId(z.id)}
                                    className={`px-4 py-2 text-sm border transition-colors ${
                                        z.id === activeId ? "bg-cb-accent border-cb-accent text-white" : "border-white/15 text-white/70 hover:border-cb-accent"
                                    }`}
                                >
                                    {z[lang].name}
                                </button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.id}
                                data-testid="map-zone-panel"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.35 }}
                            >
                                <div className="flex items-center gap-3 text-cb-accent">
                                    <MapPin size={20} />
                                    <span className="text-xs uppercase tracking-[0.25em]">{active[lang].region}</span>
                                </div>
                                <h3 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tight">{active[lang].name}</h3>
                                <div className="mt-2 text-sm text-slate-400">{active.works.length} {m.projectsWord}</div>

                                <div className="mt-8 text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{m.works}</div>
                                <div className="space-y-px bg-white/5">
                                    {active.works.map((w, i) => (
                                        <div key={i} className="bg-cb-dark p-5">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {w.cats.map((cslug) => (
                                                        <span key={cslug} className="text-[10px] uppercase tracking-wider bg-cb-accent/15 text-cb-accent px-2 py-1">
                                                            {catName(cslug)}
                                                        </span>
                                                    ))}
                                                </div>
                                                <span className="font-display text-cb-accent text-sm font-semibold shrink-0 whitespace-nowrap">{w.year}</span>
                                            </div>
                                            <div className="mt-3 text-sm leading-relaxed text-slate-200">{w[lang]}</div>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <button
                            data-testid="map-cta"
                            onClick={() => navigate("/progetti")}
                            className="cb-glow mt-8 inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-7 py-3.5 font-medium transition-colors"
                        >
                            {t.portfolio.view} <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
